/**
 * Progressive signup: email (step 1) → optional SMS (step 2) → optional push (step 3).
 *
 * Each channel is captured and audited separately in Klaviyo. Step 1 posts
 * to /client/subscriptions/ (list relationship). Steps 2–3 use
 * /client/profiles/ to upsert the same profile without re-subscribing email.
 */
(function () {
  'use strict';

  var CONSENT_VERSION = '2026-05-21-bcc-progressive-v1';

  function isValidEmail(value) {
    return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  function isLooselyValidPhone(value) {
    var trimmed = (value || '').trim();
    if (!trimmed) return false;
    var stripped = trimmed.replace(/[\s\-().]/g, '');
    return /^\+?\d{8,15}$/.test(stripped);
  }

  function normalizeToE164(value) {
    var trimmed = (value || '').trim();
    if (!trimmed) return '';
    if (/^\+\d+$/.test(trimmed)) return trimmed;
    var stripped = trimmed.replace(/[\s\-().]/g, '');
    if (!stripped) return '';
    if (stripped.charAt(0) === '+') return stripped;
    if (/^\d{10,15}$/.test(stripped)) return '+' + stripped;
    return stripped;
  }

  function resolveRoot(el) {
    if (!el) return null;
    if (el.matches && el.matches('[data-progressive-signup]')) return el;
    if (el.querySelector) return el.querySelector('[data-progressive-signup]');
    return null;
  }

  function getStep(root, name) {
    return root.querySelector('[data-ps-step="' + name + '"]');
  }

  function showStep(root, name) {
    var steps = root.querySelectorAll('[data-ps-step]');
    for (var i = 0; i < steps.length; i++) {
      var step = steps[i];
      var isActive = step.getAttribute('data-ps-step') === name;
      step.hidden = !isActive;
    }
    root.dataset.psActiveStep = name;

    var announcer = root.querySelector('[data-ps-step-announcer]');
    if (announcer) {
      var labels = {
        email: 'Email signup',
        sms: 'Optional text alerts',
        push: 'Optional notifications',
        done: 'Signup complete',
        complete: 'Signup complete'
      };
      announcer.textContent = labels[name] || '';
    }
  }

  function buildBaseMetadata(root) {
    return {
      source: root.dataset.psSource || '',
      campaignName: root.dataset.psCampaign || '',
      consentTextVersion: root.dataset.psConsentVersion || CONSENT_VERSION,
      pageUrl: typeof window !== 'undefined' && window.location ? window.location.href : '',
      timestamp: new Date().toISOString(),
      emailConsentText: root.dataset.psEmailConsentText || '',
      smsConsentText: root.dataset.psSmsConsentText || ''
    };
  }

  function buildEmailConsentProperties(emailConsent, metadata) {
    return {
      'Email Marketing Consent': emailConsent ? 'SUBSCRIBED' : 'NOT_SUBSCRIBED',
      'SMS Marketing Consent': 'NOT_SUBSCRIBED',
      'Push Marketing Consent': 'NOT_SUBSCRIBED',
      'Campaign Name': metadata.campaignName || '',
      'Last Consent Form': metadata.source || '',
      'Last Email Consent Form': metadata.source || '',
      'Last Consent Text Version': metadata.consentTextVersion || '',
      'Last Email Consent Text Version': metadata.consentTextVersion || '',
      'Last Consent Page URL': metadata.pageUrl || '',
      'Last Email Consent Timestamp': metadata.timestamp || '',
      'Last Consent Timestamp': metadata.timestamp || '',
      'Last Email Consent Text': metadata.emailConsentText || ''
    };
  }

  function buildSmsConsentProperties(smsConsent, metadata) {
    return {
      'SMS Marketing Consent': smsConsent ? 'SUBSCRIBED' : 'NOT_SUBSCRIBED',
      'Last SMS Consent Form': metadata.source || '',
      'Last SMS Consent Text Version': metadata.consentTextVersion || '',
      'Last SMS Consent Page URL': metadata.pageUrl || '',
      'Last SMS Consent Timestamp': metadata.timestamp || '',
      'Last Consent Timestamp': metadata.timestamp || '',
      'Last SMS Consent Text': metadata.smsConsentText || '',
      'Campaign Name': metadata.campaignName || ''
    };
  }

  function klaviyoSubscribe(config, profileAttributes, customSource) {
    var endpoint = 'https://a.klaviyo.com/client/subscriptions/?company_id=' +
      encodeURIComponent(config.klaviyoCompanyId);

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: customSource || config.source,
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes
            }
          }
        },
        relationships: {
          list: {
            data: { type: 'list', id: config.klaviyoListId }
          }
        }
      }
    };

    return fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'revision': config.klaviyoRevision || '2026-04-15'
      },
      body: JSON.stringify(payload)
    });
  }

  function klaviyoUpsertProfile(config, profileAttributes) {
    var endpoint = 'https://a.klaviyo.com/client/profiles/?company_id=' +
      encodeURIComponent(config.klaviyoCompanyId);

    return fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'revision': config.klaviyoRevision || '2026-04-15'
      },
      body: JSON.stringify({
        data: { type: 'profile', attributes: profileAttributes }
      }),
      keepalive: true
    });
  }

  function validateEmailStep(root) {
    var emailInput = root.querySelector('[data-ps-email]');
    var zipInput = root.querySelector('[data-ps-zip]');
    var emailCheckbox = root.querySelector('[data-ps-email-checkbox]');
    var errorEl = root.querySelector('[data-ps-email-error]');

    function fail(msg, focusEl) {
      if (errorEl) {
        errorEl.textContent = msg;
        errorEl.hidden = false;
      }
      if (focusEl && typeof focusEl.focus === 'function') focusEl.focus();
      return { ok: false, error: msg };
    }

    if (errorEl) errorEl.hidden = true;
    if (emailInput) {
      emailInput.classList.remove('is-invalid');
      emailInput.removeAttribute('aria-invalid');
    }
    if (zipInput) {
      zipInput.classList.remove('is-invalid');
      zipInput.removeAttribute('aria-invalid');
    }
    if (emailCheckbox) {
      emailCheckbox.classList.remove('is-invalid');
      emailCheckbox.removeAttribute('aria-invalid');
    }

    var email = emailInput ? emailInput.value.trim() : '';
    if (!email || !isValidEmail(email)) {
      if (emailInput) {
        emailInput.classList.add('is-invalid');
        emailInput.setAttribute('aria-invalid', 'true');
      }
      return fail('Please enter a valid email address.', emailInput);
    }

    var zip = '';
    if (zipInput) {
      var zipRaw = zipInput.value.trim();
      if (!/^\d{5}(-\d{4})?$/.test(zipRaw)) {
        zipInput.classList.add('is-invalid');
        zipInput.setAttribute('aria-invalid', 'true');
        return fail(root.dataset.psZipError || 'Please enter a 5-digit ZIP code.', zipInput);
      }
      zip = zipRaw.slice(0, 5);
    }

    if (!emailCheckbox || !emailCheckbox.checked) {
      if (emailCheckbox) {
        emailCheckbox.classList.add('is-invalid');
        emailCheckbox.setAttribute('aria-invalid', 'true');
      }
      var consentMsg = root.dataset.psConsentError ||
        'Please tick the box to receive email updates.';
      return fail(consentMsg, emailCheckbox);
    }

    return { ok: true, email: email, zip: zip };
  }

  function validateSmsStep(root) {
    var smsCheckbox = root.querySelector('[data-ps-sms-checkbox]');
    var phoneInput = root.querySelector('[data-ps-phone]');
    var errorEl = root.querySelector('[data-ps-sms-error]');

    if (errorEl) errorEl.hidden = true;
    if (phoneInput) {
      phoneInput.classList.remove('is-invalid');
      phoneInput.removeAttribute('aria-invalid');
    }

    if (!smsCheckbox || !smsCheckbox.checked) {
      return { ok: true, smsConsent: false, phone: '', phoneE164: '' };
    }

    var phoneRaw = phoneInput ? phoneInput.value.trim() : '';
    if (!phoneRaw) {
      if (phoneInput) {
        phoneInput.classList.add('is-invalid');
        phoneInput.setAttribute('aria-invalid', 'true');
        phoneInput.focus();
      }
      if (errorEl) {
        errorEl.textContent = 'Please enter your phone number to receive text alerts.';
        errorEl.hidden = false;
      }
      return { ok: false, error: 'Phone required for SMS.' };
    }

    if (!isLooselyValidPhone(phoneRaw)) {
      if (phoneInput) {
        phoneInput.classList.add('is-invalid');
        phoneInput.setAttribute('aria-invalid', 'true');
        phoneInput.focus();
      }
      if (errorEl) {
        errorEl.textContent = 'Please enter a valid phone number with country code (e.g. +64 21 123 4567).';
        errorEl.hidden = false;
      }
      return { ok: false, error: 'Invalid phone.' };
    }

    return {
      ok: true,
      smsConsent: true,
      phone: phoneRaw,
      phoneE164: normalizeToE164(phoneRaw)
    };
  }

  function submitEmailStep(root, config) {
    var check = validateEmailStep(root);
    if (!check.ok) return Promise.reject(new Error(check.error));

    var metadata = buildBaseMetadata(root);
    var emailConsent = true;
    var properties = buildEmailConsentProperties(emailConsent, metadata);
    if (config.profileProperties && typeof config.profileProperties === 'object') {
      Object.keys(config.profileProperties).forEach(function (key) {
        properties[key] = config.profileProperties[key];
      });
    }
    var profileAttributes = {
      email: check.email,
      properties: properties,
      subscriptions: {
        email: { marketing: { consent: 'SUBSCRIBED' } }
      }
    };
    if (check.zip) {
      profileAttributes.location = { zip: check.zip, country: 'United States' };
    }

    return klaviyoSubscribe(config, profileAttributes, config.source).then(function (response) {
      if (response.status === 202 || response.status === 200) {
        root.dataset.psCapturedEmail = check.email;
        if (check.zip) root.dataset.psCapturedZip = check.zip;
        return { email: check.email };
      }
      var fallback = root.dataset.psKlaviyoError ||
        'Sorry, something went wrong. Please try again.';
      return response.json().then(function (body) {
        var detail = (body && body.errors && body.errors[0] && body.errors[0].detail) ||
          fallback;
        throw new Error(detail);
      }).catch(function (err) {
        if (err instanceof Error) throw err;
        throw new Error(fallback);
      });
    });
  }

  function submitSmsStep(root, config) {
    var email = root.dataset.psCapturedEmail || '';
    if (!email) return Promise.resolve({ skipped: true });

    var check = validateSmsStep(root);
    if (!check.ok) return Promise.reject(new Error(check.error));

    if (!check.smsConsent) {
      return Promise.resolve({ smsConsent: false });
    }

    var metadata = buildBaseMetadata(root);
    metadata.timestamp = new Date().toISOString();

    var profileAttributes = {
      email: email,
      phone_number: check.phoneE164,
      properties: buildSmsConsentProperties(true, metadata),
      subscriptions: {
        sms: { marketing: { consent: 'SUBSCRIBED' } }
      }
    };

    return klaviyoUpsertProfile(config, profileAttributes).then(function () {
      root.dataset.psCapturedPhone = check.phoneE164;
      return { smsConsent: true, phone: check.phoneE164 };
    });
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function phoneTail(e164) {
    if (!e164) return '';
    var digits = String(e164).replace(/\D/g, '');
    return digits.length >= 4 ? digits.slice(-4) : digits;
  }

  function updateSuccessCopy(root, stepName) {
    var email = root.dataset.psCapturedEmail || '';
    var phone = root.dataset.psCapturedPhone || '';
    var variant = root.dataset.psFlowVariant || 'bcc';

    if (stepName === 'done') {
      var heading = root.querySelector('[data-ps-done-heading]');
      var body = root.querySelector('[data-ps-done-message]');
      if (!body) return;
      if (heading) heading.textContent = "You're in.";
      var msg = email
        ? 'We\u2019ll email you at <strong>' + escapeHtml(email) + '</strong> as we approach the final boxes.'
        : 'We\u2019ll be in touch as we approach the final boxes.';
      if (phone) {
        msg += ' Text alerts will go to the number ending in ' + escapeHtml(phoneTail(phone)) + '.';
      }
      body.innerHTML = msg;
      return;
    }

    if (stepName === 'complete') {
      var completeMsg = root.querySelector('.progressive-signup__complete-msg');
      if (!completeMsg) return;
      if (email) {
        completeMsg.innerHTML =
          'You\u2019re on the list. We\u2019ll email <strong>' + escapeHtml(email) + '</strong> with offers, drops and restocks.';
      } else {
        completeMsg.textContent = 'You\u2019re on the list.';
      }
      if (phone) {
        completeMsg.innerHTML += ' Text alerts will go to the number ending in ' +
          escapeHtml(phoneTail(phone)) + '.';
      }
    }
  }

  function finalizeProgressiveFlow(root, config, payload) {
    payload = payload || {};
    var email = root.dataset.psCapturedEmail || payload.email || '';
    var phone = root.dataset.psCapturedPhone || payload.phone || '';
    var step = 'done';

    if (root.dataset.psFlowVariant === 'footer' && getStep(root, 'complete')) {
      step = 'complete';
    } else if (!getStep(root, 'done')) {
      step = getStep(root, 'complete') ? 'complete' : 'email';
    }

    showStep(root, step);
    updateSuccessCopy(root, step);

    if (typeof config.onComplete === 'function') {
      config.onComplete({
        email: email,
        phone: phone,
        step: step
      });
    }
  }

  function revealFooterSmsStep(root) {
    var banner = root.querySelector('[data-ps-sms-success-banner]');
    if (banner) banner.hidden = false;
    var smsHeading = root.querySelector('[data-ps-sms-heading]');
    var smsBody = root.querySelector('[data-ps-sms-body]');
    if (smsHeading) smsHeading.hidden = true;
    if (smsBody) smsBody.hidden = true;
  }

  function syncShopifyCustomer(root, email, zip) {
    if (!email) return Promise.resolve();
    var host = root.closest('[data-footer-signup-root]');
    if (!host) return Promise.resolve();
    var syncForm = host.querySelector('[data-footer-shopify-sync]');
    if (!syncForm) return Promise.resolve();

    var emailInput = syncForm.querySelector('[data-footer-shopify-email]');
    if (emailInput) emailInput.value = email;
    var tagsInput = syncForm.querySelector('input[name="contact[tags]"]');
    if (tagsInput && zip) {
      if (!tagsInput.dataset.baseTags) tagsInput.dataset.baseTags = tagsInput.value;
      tagsInput.value = tagsInput.dataset.baseTags + ',zip-' + zip;
    }

    try {
      if (typeof syncForm.requestSubmit === 'function') {
        syncForm.requestSubmit();
        return Promise.resolve();
      }
      syncForm.submit();
    } catch (err) { /* fail quietly — Klaviyo is primary */ }
    return Promise.resolve();
  }

  function initPushStep(root, config) {
    var pushStep = getStep(root, 'push');
    if (!pushStep) {
      finalizeProgressiveFlow(root, config);
      return;
    }

    var pushRoot = pushStep.querySelector('[data-push-consent]');
    var email = root.dataset.psCapturedEmail || '';
    var phone = root.dataset.psCapturedPhone || '';

    if (!window.SidekickPushConsent || !window.SidekickPushConsent.isSupported ||
        !window.SidekickPushConsent.isSupported()) {
      finalizeProgressiveFlow(root, config);
      return;
    }

    var perm = window.SidekickPushConsent.getCurrentPermission();
    if (perm === 'granted' || perm === 'denied') {
      finalizeProgressiveFlow(root, config);
      return;
    }

    showStep(root, 'push');
    if (pushRoot) {
      pushRoot.hidden = false;
      window.SidekickPushConsent.init(pushRoot, {
        email: email,
        phone: phone,
        country: '',
        klaviyoCompanyId: config.klaviyoCompanyId,
        klaviyoRevision: config.klaviyoRevision,
        source: config.source,
        campaignName: config.campaignName || root.dataset.psCampaign || '',
        pushConsentText: root.dataset.psPushConsentText || '',
        pushConsentVersion: root.dataset.psConsentVersion || CONSENT_VERSION,
        onComplete: function () {
          finalizeProgressiveFlow(root, config, { email: email, phone: phone });
        }
      });
    } else {
      finalizeProgressiveFlow(root, config, { email: email, phone: phone });
    }
  }

  function wireStepHandlers(root, config) {
    if (root.dataset.psWired === '1') return;
    root.dataset.psWired = '1';

    var emailForm = root.querySelector('[data-ps-email-form]');
    var smsForm = root.querySelector('[data-ps-sms-form]');
    var emailSubmit = root.querySelector('[data-ps-email-submit]');
    var emailLabel = root.querySelector('[data-ps-email-submit-label]');
    var emailSpinner = root.querySelector('[data-ps-email-spinner]');
    var smsSubmit = root.querySelector('[data-ps-sms-submit]');
    var smsLabel = root.querySelector('[data-ps-sms-submit-label]');
    var smsSpinner = root.querySelector('[data-ps-sms-spinner]');

    function setEmailLoading(yes) {
      if (!emailSubmit) return;
      if (yes) {
        emailSubmit.setAttribute('disabled', 'true');
        if (emailLabel) emailLabel.textContent = 'Sending';
        if (emailSpinner) emailSpinner.hidden = false;
      } else {
        emailSubmit.removeAttribute('disabled');
        if (emailLabel) emailLabel.textContent = root.dataset.psEmailCta || 'Send me the alert';
        if (emailSpinner) emailSpinner.hidden = true;
      }
    }

    function setSmsLoading(yes) {
      if (!smsSubmit) return;
      if (yes) {
        smsSubmit.setAttribute('disabled', 'true');
        if (smsLabel) smsLabel.textContent = 'Sending';
        if (smsSpinner) smsSpinner.hidden = false;
      } else {
        smsSubmit.removeAttribute('disabled');
        if (smsLabel) smsLabel.textContent = root.dataset.psSmsCta || 'Get text alerts';
        if (smsSpinner) smsSpinner.hidden = true;
      }
    }

    if (emailForm) {
      emailForm.addEventListener('submit', function (e) {
        e.preventDefault();
        setEmailLoading(true);
        submitEmailStep(root, config)
          .then(function () {
            setEmailLoading(false);
            var capturedEmail = root.dataset.psCapturedEmail || '';
            if (config.syncShopify !== false) {
              syncShopifyCustomer(root, capturedEmail, root.dataset.psCapturedZip || '');
            }
            if (typeof config.onEmailComplete === 'function') {
              config.onEmailComplete({ email: capturedEmail });
            }
            if (root.dataset.psEmailOnly === 'true') {
              finalizeProgressiveFlow(root, config, { email: capturedEmail });
              return;
            }
            if (root.dataset.psFlowVariant === 'footer') {
              revealFooterSmsStep(root);
            }
            showStep(root, 'sms');
            var phoneInput = root.querySelector('[data-ps-phone]');
            if (phoneInput && typeof phoneInput.focus === 'function') {
              setTimeout(function () { phoneInput.focus(); }, 80);
            }
          })
          .catch(function (err) {
            setEmailLoading(false);
            var errorEl = root.querySelector('[data-ps-email-error]');
            var fallback = root.dataset.psKlaviyoError || 'Something went wrong. Please try again.';
            if (errorEl) {
              errorEl.textContent = err && err.message ? err.message : fallback;
              errorEl.hidden = false;
            }
          });
      });
    }

    if (smsForm) {
      smsForm.addEventListener('submit', function (e) {
        e.preventDefault();
        setSmsLoading(true);
        submitSmsStep(root, config)
          .then(function () {
            setSmsLoading(false);
            initPushStep(root, config);
          })
          .catch(function (err) {
            setSmsLoading(false);
            var errorEl = root.querySelector('[data-ps-sms-error]');
            var fallback = root.dataset.psKlaviyoError || 'Something went wrong. Please try again.';
            if (errorEl) {
              errorEl.textContent = err && err.message ? err.message : fallback;
              errorEl.hidden = false;
            }
          });
      });
    }

    var smsSkip = root.querySelector('[data-ps-sms-skip]');
    if (smsSkip) {
      smsSkip.addEventListener('click', function (e) {
        e.preventDefault();
        initPushStep(root, config);
      });
    }

    var emailCheckbox = root.querySelector('[data-ps-email-checkbox]');
    var smsCheckbox = root.querySelector('[data-ps-sms-checkbox]');
    var phoneInput = root.querySelector('[data-ps-phone]');

    if (emailCheckbox) {
      emailCheckbox.addEventListener('change', function () {
        emailCheckbox.classList.remove('is-invalid');
        emailCheckbox.removeAttribute('aria-invalid');
      });
    }
    if (smsCheckbox) {
      smsCheckbox.addEventListener('change', function () {
        smsCheckbox.classList.remove('is-invalid');
        if (smsCheckbox.checked && phoneInput && !phoneInput.value.trim()) {
          phoneInput.focus();
        }
      });
    }
    if (phoneInput) {
      phoneInput.addEventListener('input', function () {
        phoneInput.classList.remove('is-invalid');
        phoneInput.removeAttribute('aria-invalid');
      });
    }
  }

  function init(hostEl, config) {
    config = config || {};
    var root = resolveRoot(hostEl);
    if (!root) return null;

    if (!config.klaviyoCompanyId || !config.klaviyoListId) return null;

    config.source = config.source || root.dataset.psSource || '';
    config.campaignName = config.campaignName || root.dataset.psCampaign || '';

    wireStepHandlers(root, config);
    showStep(root, 'email');

    return {
      root: root,
      showStep: function (name) { showStep(root, name); },
      submitEmailStep: function () { return submitEmailStep(root, config); }
    };
  }

  window.SidekickProgressiveSignup = {
    init: init,
    validateEmailStep: validateEmailStep,
    validateSmsStep: validateSmsStep,
    submitEmailStep: submitEmailStep,
    submitSmsStep: submitSmsStep,
    showStep: function (rootOrHost, stepName) {
      var root = resolveRoot(rootOrHost);
      if (root) showStep(root, stepName);
    },
    isValidEmail: isValidEmail,
    normalizeToE164: normalizeToE164
  };
})();
